import { useEffect, useRef, useCallback } from 'react';
import { getWsUrl } from '../config/api';
import type { Bot, GridBotStatus, LiveTrade } from '../types/trading';

// Re-export Strategy as Bot for compatibility
export type Strategy = Bot;

interface WSEvent {
  type: string;
  run_id?: string;
  symbol?: string;
  timestamp: number;
  data?: Record<string, any>;
  strategies?: Bot[];
}

interface UseTradingWebSocketOptions {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onStrategiesUpdate?: (strategies: Bot[]) => void;
  onGridStatusUpdate?: (runId: string, status: GridBotStatus) => void;
  onPriceUpdate?: (symbol: string, price: number) => void;
  onTradeExecuted?: (runId: string, trade: LiveTrade) => void;
  onBotEvent?: (type: string, runId: string) => void;
}

export function useTradingWebSocket(options: UseTradingWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalCloseRef = useRef(false);

  // Store callbacks in refs to avoid reconnection on callback changes
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (intentionalCloseRef.current) return; // Don't reconnect if intentionally closed

    const ws = new WebSocket(getWsUrl('/ws'));

    ws.onopen = () => {
      console.log('[WS] Connected to trading engine');
      callbacksRef.current.onConnected?.();
    };

    ws.onmessage = (event) => {
      try {
        const msg: WSEvent = JSON.parse(event.data);

        switch (msg.type) {
          case 'initial_state':
            if (msg.strategies) {
              callbacksRef.current.onStrategiesUpdate?.(msg.strategies);
            }
            break;

          case 'grid_started':
          case 'grid_status':
            if (msg.run_id && msg.data) {
              console.log(`[WS] Grid status for ${msg.run_id}:`, msg.data);
              callbacksRef.current.onGridStatusUpdate?.(
                msg.run_id,
                msg.data as unknown as GridBotStatus
              );
            }
            break;

          case 'price_update':
          case 'last_price':
            if (msg.data?.price && msg.symbol) {
              callbacksRef.current.onPriceUpdate?.(msg.symbol, msg.data.price);
            }
            // Handle direct price field from broker stream
            if ((msg as any).price && ((msg as any).figi || msg.symbol)) {
              const key = (msg as any).figi || msg.symbol;
              callbacksRef.current.onPriceUpdate?.(key, (msg as any).price);
            }
            break;

          case 'order_filled':
            console.log(`[WS] Order filled: ${msg.data?.side} at ${msg.data?.price}`);
            if (msg.run_id && msg.data) {
              const trade: LiveTrade = {
                id: `${msg.run_id}-${Date.now()}`,
                time: Math.floor(msg.timestamp / 1000),
                type: msg.data.side as 'buy' | 'sell',
                price: msg.data.price,
                quantity: msg.data.quantity,
                pnl: msg.data.pnl,
              };
              callbacksRef.current.onTradeExecuted?.(msg.run_id, trade);
            }
            break;

          case 'bot_started':
            console.log(`[WS] Bot started: ${msg.run_id}`);
            if (msg.run_id) {
              callbacksRef.current.onBotEvent?.('started', msg.run_id);
            }
            break;

          case 'bot_stopped':
          case 'stop_loss':
          case 'take_profit':
            console.log(`[WS] Bot event: ${msg.type} for ${msg.run_id}`);
            if (msg.run_id) {
              callbacksRef.current.onBotEvent?.(msg.type, msg.run_id);
            }
            break;

          default:
            console.log('[WS] Unknown event:', msg.type);
        }
      } catch (err) {
        console.error('[WS] Parse error:', err);
      }
    };

    ws.onclose = () => {
      console.log('[WS] Disconnected');
      callbacksRef.current.onDisconnected?.();
      wsRef.current = null;

      // Only auto-reconnect if NOT intentionally closed
      if (!intentionalCloseRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          console.log('[WS] Reconnecting...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (err) => {
      console.error('[WS] Error:', err);
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    intentionalCloseRef.current = false;
    connect();

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };
}
